import React from 'react';
import PropTypes from 'prop-types';
import { useMouseCoordinates } from '../MouseCoordinatesWrapper';
import { Body4 } from '../../../../../../atomic/text/Text';
import style from './HoverBox.module.css';
import ReactDOM from 'react-dom';
import { roundToTick, computePnlAtPrice, formatPnl } from './hoverBoxUtils';

export const HoverBox = ({
  json,
  topBoundary,
  bottomBoundary,
  leftBoundary,
  rightBoundary,
  portalRef,
  size,
  // Position metrics for P&L display
  linePrice,
  tickSize,
  fillPrice,
  openQuantity,
  priceMultiplier,
  realizedPnl,
  isLong,
  delta,
  // qty that fills at this specific line level (for ratchet bracket)
  levelQty,
}) => {
  const { coordinates, showCrossHairs } = useMouseCoordinates();
  const [x, y] = coordinates;
  if (!portalRef || !portalRef.current) return null;

  // Build the enriched display — rounded price + P&L rows
  const displayPrice = linePrice != null ? roundToTick(linePrice, tickSize) : null;

  const pnlAtLine = computePnlAtPrice({
    linePrice,
    fillPrice,
    quantity: levelQty ?? openQuantity,
    priceMultiplier,
    isLong,
    delta,
  });

  // Projected state after fill at this line (for the full open quantity)
  let projRealizedPnl = null;
  let projUnrealizedPnl = null;
  let projRemainingQty = null;

  if (pnlAtLine != null && realizedPnl != null && openQuantity != null) {
    const filledQty = levelQty ?? openQuantity;
    projRealizedPnl = realizedPnl + pnlAtLine;
    projRemainingQty = openQuantity - filledQty;
    // uPnL after fill: remaining qty still at open price vs linePrice (same direction)
    if (projRemainingQty > 0 && fillPrice != null && priceMultiplier != null) {
      const uPriceDelta = delta != null
        ? delta * (linePrice - fillPrice)
        : linePrice - fillPrice;
      const dirFactor = isLong ? 1 : -1;
      projUnrealizedPnl = dirFactor * uPriceDelta * projRemainingQty * priceMultiplier;
    } else {
      projUnrealizedPnl = 0;
    }
  }

  return ReactDOM.createPortal(
    <>
      {x > leftBoundary &&
      x < rightBoundary &&
      y > topBoundary &&
      y < bottomBoundary &&
      showCrossHairs ? (
          <foreignObject
            id="hoverBox"
            x={x > size / 2 ? x - 140 : x}
            y={y > size / 2 ? y - 120 : y}
            width={140}
            height={240}
          >
            <div className={style.box}>
              {/* Static fields from json (Category, Type, etc.) */}
              {Object.entries(json)
                .filter(([key]) => key !== 'Break Price' && key !== 'Price')
                .map((entry) => (
                  <Body4 key={entry[0]}>{`${entry[0]}: ${entry[1]}`}</Body4>
                ))}

              {/* Rounded price */}
              {displayPrice != null && (
                <Body4>{`Price: ${displayPrice}`}</Body4>
              )}

              {/* P&L at this line */}
              {pnlAtLine != null && (
                <Body4>{`PnL: ${formatPnl(pnlAtLine)}`}</Body4>
              )}

              {/* Projected state after fill */}
              {projRealizedPnl != null && (
                <Body4>{`→ rPnL: ${formatPnl(projRealizedPnl)}`}</Body4>
              )}
              {projUnrealizedPnl != null && (
                <Body4>{`→ uPnL: ${formatPnl(projUnrealizedPnl)}`}</Body4>
              )}
              {projRemainingQty != null && (
                <Body4>{`→ Qty: ${projRemainingQty}`}</Body4>
              )}
            </div>
          </foreignObject>
        ) : null}
    </>,
    portalRef.current
  );
};

HoverBox.propTypes = {
  json: PropTypes.object,
  topBoundary: PropTypes.any,
  bottomBoundary: PropTypes.any,
  leftBoundary: PropTypes.any,
  rightBoundary: PropTypes.any,
  portalRef: PropTypes.object,
  size: PropTypes.number,
  linePrice: PropTypes.number,
  tickSize: PropTypes.number,
  fillPrice: PropTypes.number,
  openQuantity: PropTypes.number,
  priceMultiplier: PropTypes.number,
  realizedPnl: PropTypes.number,
  isLong: PropTypes.bool,
  delta: PropTypes.number,
  levelQty: PropTypes.number,
};
